import { FC, useEffect, useState } from 'react';
import { allSettled } from 'effector';
import { useUnit } from 'effector-react/scope';
import { CircularProgress } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { BaseInputLabel, Divider } from '@ui/index';
import { FieldProps } from '../../interfaces';
import { $data, $isLoading, searchChanged, getList, createScope } from '../../models';
import { checkIdsWithChildren } from './helpers/checkIdsWithChildren';
import { getClickedItem } from './helpers/getClickedItem';
import {
  getFullSelected,
  getIndeterminate,
} from './helpers/getIndeterminateAndFullSelected';
import { getTreeData, TreeItem } from './helpers/getTreeData';
import * as Styled from './styled';
import { TreeItems } from './treeItems';

export const TypeSimpleTree: FC<FieldProps> = ({
  id,
  onChange,
  label,
  divider,
  value,
  activeIds,
  ticketsCountByIds,
  withAcrchived,
  showAcrchived,
  search,
  checkboxSelection,
  multiSelect,
  disabled,
}) => {
  const [isLoading, data] = useUnit([$isLoading, $data]);
  const [treeGettingLoading, setTreeGettingLoading] = useState(false);
  const [itemClick, setItemClick] = useState(0);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [indeterminateItems, setIndeterminateItems] = useState<string[]>([]);
  const [treeData, setTreeData] = useState<TreeItem[]>([]);
  const [notArchivedItems, setNotArchivedItems] = useState<string[]>([]);

  const scope = createScope(id);

  const resolveItemsAfterClick = (ids: string[]) => {
    const clickedItem = getClickedItem(ids, selectedItems);

    const selected = checkIdsWithChildren(ids, treeData, String(clickedItem));

    const indeterminate = getIndeterminate(selected, treeData);
    setIndeterminateItems(indeterminate);

    const fullSelected = getFullSelected(selected, treeData);
    const allSelected = [...new Set([...selected, ...fullSelected])];
    setSelectedItems(
      activeIds ? allSelected.filter((id) => activeIds.includes(id)) : allSelected
    );

    const expanded = [...expandedItems, ...selected, ...fullSelected, ...indeterminate];
    setExpandedItems([...new Set(expanded)]);
  };

  const handleSelectedItemsChange = (_: React.SyntheticEvent, ids: string | string[]) => {
    resolveItemsAfterClick(Array.isArray(ids) ? ids : [ids]);

    setItemClick((prev) => prev + 1);
  };

  const handleExpandedItemsChange = (_: React.SyntheticEvent, ids: string[]) => {
    setExpandedItems(ids);
  };

  const handleSelectedSingleItemChange = (_: React.SyntheticEvent, id: string | null) => {
    setSelectedItems(id ? [id] : []);

    setItemClick((prev) => prev + 1);
  };

  const loadingOrTreeGetting = isLoading || treeGettingLoading;

  useEffect(() => {
    setTreeGettingLoading(false);
  }, [treeData]);

  useEffect(() => {
    if (!itemClick) return;

    onChange?.(selectedItems);
  }, [itemClick]);

  useEffect(() => {
    if (!treeData.length) return;

    resolveItemsAfterClick(value ?? []);
  }, [value, treeData]);

  useEffect(() => {
    setTreeGettingLoading(true);

    const dataResolveArchived = showAcrchived
      ? data
      : data.filter(({ archived }) => !archived);

    const tree = getTreeData(dataResolveArchived, null, activeIds);
    setTreeData(tree);
  }, [data, showAcrchived]);

  useEffect(() => {
    setNotArchivedItems(
      data.filter(({ archived }) => !archived).map(({ id }) => String(id))
    );
  }, [data]);

  useEffect(() => {
    setSelectedItems((prev) => prev.filter((id) => notArchivedItems.includes(id)));

    setItemClick((prev) => prev + 1);
  }, [showAcrchived]);

  useEffect(() => {
    const handleSearchChanged = async (params: string) => {
      await allSettled(searchChanged, { scope, params });
    };

    handleSearchChanged(search ?? '');
  }, [search]);

  useEffect(() => {
    const handleStartRequest = async () => {
      await allSettled(getList, {
        scope,
        params: { withAcrchived },
      });
    };

    handleStartRequest();
  }, []);

  const resolveMultiSelect = multiSelect ?? true;

  return (
    <Styled.Wrapper>
      {label && <BaseInputLabel>{label}</BaseInputLabel>}

      {loadingOrTreeGetting && <CircularProgress size={'20px'} />}

      {resolveMultiSelect && (
        <SimpleTreeView
          multiSelect={true}
          checkboxSelection={checkboxSelection ?? true}
          onSelectedItemsChange={handleSelectedItemsChange}
          selectedItems={selectedItems}
          onExpandedItemsChange={handleExpandedItemsChange}
          expandedItems={expandedItems}
          disableSelection={disabled}
        >
          {!isLoading && (
            <TreeItems
              data={treeData}
              indeterminate={indeterminateItems}
              ticketsCountByIds={ticketsCountByIds}
              selectedItems={selectedItems}
              disabled={disabled}
            />
          )}
        </SimpleTreeView>
      )}

      {!resolveMultiSelect && (
        <SimpleTreeView
          key={selectedItems?.[0]}
          selectedItems={selectedItems?.[0]}
          multiSelect={false}
          onSelectedItemsChange={handleSelectedSingleItemChange}
          checkboxSelection={checkboxSelection ?? true}
          onExpandedItemsChange={handleExpandedItemsChange}
          expandedItems={expandedItems}
          disableSelection={disabled}
        >
          {!isLoading && (
            <TreeItems
              data={treeData}
              indeterminate={indeterminateItems}
              ticketsCountByIds={ticketsCountByIds}
              selectedItems={selectedItems}
              disabled={disabled}
            />
          )}
        </SimpleTreeView>
      )}

      <Divider isVisible={divider} />
    </Styled.Wrapper>
  );
};
